import SessionCache from '@plugins/cache';
import { defineStore } from 'pinia';
import { useServiceStore } from '@modules/service/m-service';

const cache = new SessionCache('signatures');

export const useSignaturesStore = defineStore('m-signatures', {
	state: () => ({
		numberOfPendingOperations: 0,
	}),

	actions: {
		setNumberOfPendingOperations(value) {
			this.numberOfPendingOperations = value;
		},
		fetch({ status, paginationKey, refresh, ebContract }) {
			const cacheKey = `list/${status}`;
			const queryParams = {};
			const url = '/signature';
			const method = 'GET';

			const statusAux = status === 'pending' ? 'pending|SIGNED_ON_BACKOFFICE' : status;

			/* istanbul ignore next */
			if (refresh) {
				SessionCache.clear('signatures');
			}

			if (cache.has(cacheKey) && !paginationKey) {
				return cache.get(cacheKey);
			}

			if (statusAux) {
				Object.assign(queryParams, { status: statusAux.toUpperCase() });
			}

			if (paginationKey) {
				Object.assign(queryParams, { paginationKey });
			}

			if (ebContract) {
				Object.assign(queryParams, { ebContract });
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => {
					// If cache and new items, we add them to the end of the list
					if (cache.has(cacheKey)) {
						const result = cache.get(cacheKey);
						data?.data?.forEach((item) => {
							const found = result.data.find(
								({ signatureId }) => signatureId === item.signatureId
							);

							/* istanbul ignore else */
							if (!found) {
								result.data.push(item);
							}
						});

						Object.assign(data?.data, result.data);
					}

					/* istanbul ignore else */
					if (status === 'pending' || status === 'Pending') {
						this.setNumberOfPendingOperations(data?.data?.length);
					} else {
						cache.set(cacheKey, data);
					}
					return data;
				});
		},

		async get({ type, signatureId }) {
			let signature = cache.get(`item/${type}/${signatureId}`);

			/* istanbul ignore else */
			if (!signature) {
				const signatures = await this.fetch({ status: type });
				signature = signatures.data.find(({ signatureId: itemId }) => itemId === signatureId);
			}

			/* istanbul ignore next */
			if (!signature?.signatureId) {
				return Promise.reject();
			}

			return signature;
		},

		sign({ type, signatureId }) {
			const url = `/signature/${signatureId}`;
			const method = 'PUT';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then((res) => {
					SessionCache.clear('signatures');
					SessionCache.clear('bizum');
					return this.fetch({ status: type }).then(() => res);
				});
		},

		delete({ type, signatureId }) {
			const url = `/signature/${signatureId}`;
			const method = 'DELETE';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(() => SessionCache.clear('signatures'))
				.then(() => this.fetch({ status: type }));
		},

		complete({ type, signatureId }) {
			const url = `/signature/${signatureId}`;
			const method = 'PATCH';

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
				})
				.then(() => SessionCache.clear('signatures'))
				.then(() => this.fetch({ status: type }));
		},

		getAssistedUserSignatures({ data, source, origin, paginationKey }) {
			const secureStore = useSecureStore();
			const queryParams = {};
			const method = 'GET';
			const userId = data?.userId;
			const url = `/signature/${userId}/signatures`;
			const userUUID = secureStore.getUuid;

			/* istanbul ignore next */
			if (paginationKey) {
				Object.assign(queryParams, {
					paginationKey,
				});
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then((res) => {
					source.postMessage(
						{
							name: 'get-user-signatures',
							payload: res.data,
							userUUID,
							userId,
						},
						origin
					);
				});
		},

		getCurrentUserSignature(signatureId) {
			const queryParams = {};
			const method = 'GET';
			const url = `/signature/currentUser/signatures/${signatureId}`;

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then(({ data }) => data);
		},

		getAssistedUserSignatureDetail({ data, source, origin, paginationKey, signatureId }) {
			const secureStore = useSecureStore();
			const queryParams = {};
			const method = 'GET';
			const userId = data?.userId;
			const url = `/signature/${userId}/signatures/${signatureId}`;
			const userUUID = secureStore.getUuid;

			/* istanbul ignore next */
			if (paginationKey) {
				Object.assign(queryParams, {
					paginationKey,
				});
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then((res) => {
					source.postMessage(
						{
							name: 'get-signature-detail',
							payload: res.data,
							userUUID,
							userId,
						},
						origin
					);
				});
		},

		getAssistedSessionSignatures({ data, source, origin, paginationKey, sessionUuid }) {
			const secureStore = useSecureStore();
			const queryParams = { sessionUuid };
			const method = 'GET';
			const userId = data?.userId;
			const url = `/signature/${userId}/signatures`;
			const userUUID = secureStore.getUuid;

			/* istanbul ignore next */
			if (paginationKey) {
				Object.assign(queryParams, {
					paginationKey,
				});
			}

			const serviceStore = useServiceStore();

			return serviceStore
				.request({
					service: {
						request: {
							url,
							method,
						},
					},
					queryParams,
				})
				.then((res) => {
					source.postMessage(
						{
							name: 'get-session-signatures',
							payload: res.data,
							userUUID,
							userId,
						},
						origin
					);
				});
		},
	},
});
